import {
  PhotogenyRejectionReasonEnum,
  PhotogenyRejectionReasonTranslate,
  PhotogenyResultEnum,
  PhotogenyResultTranslate,
} from "@/enums/photogeny";

import type { ScheduleStage, ScheduleStatus, ScheduleType } from "@/api/schedule/models";
import { ScheduleCancellationReason, SchedulePauseReason } from "@/api/schedule/models";
import { UserRoleEnum } from "@/api/user/models";
import type { Color } from "@/components/common/Tag";
import { DanceEnum, DriverLicenseEnum, MusicEnum, SkillCategoryEnum, SportsEnum } from "@/enums/skill";
import { RoutePathEnum } from "@/routes/paths";

export const SCHEDULE_TYPE_LABEL_BY_TYPE: Record<ScheduleType, string> = {
  NEW_CANDIDATE: "Novo talento",
  PHOTO_RENEWAL: "Renovação",
};

export const SCHEDULE_STAGE_LABEL_BY_STAGE: Record<ScheduleStage, string> = {
  SCHEDULED: "Agendado",
  RECEPTION: "Recepção",
  PHOTOGRAPHY: "Fotografia",
  PRODUCTION: "Produção",
  ART_PRODUCTION: "Produção artística",
};

export const SCHEDULE_STAGE_MANAGEMENT_ALLOWED_ROLES = [
  UserRoleEnum.RECEPTIONIST,
  UserRoleEnum.COORDINATOR,
  UserRoleEnum.ADMINISTRATOR,
];

export const SCHEDULE_STAGE_REQUIRED_ROLE: Partial<Record<ScheduleStage, UserRoleEnum>> = {
  PHOTOGRAPHY: UserRoleEnum.PHOTOGRAPHER,
  PRODUCTION: UserRoleEnum.PRODUCER,
  ART_PRODUCTION: UserRoleEnum.ART_PRODUCER,
};

export const SCHEDULE_STATUS_LABEL_BY_STATUS: Record<ScheduleStatus, string> = {
  AWAITING: "Aguardando",
  IN_PROGRESS: "Em atendimento",
  PAUSED: "Pausado",
  CANCELED: "Cancelado",
  COMPLETED: "Concluído",
};

export const SCHEDULE_STATUS_TAG_COLOR_BY_STATUS: Record<ScheduleStatus, Color> = {
  AWAITING: "light-gray",
  IN_PROGRESS: "notice",
  PAUSED: "alert",
  CANCELED: "error",
  COMPLETED: "neutral-success",
};

export const SCHEDULE_CANCELLATION_REASON_TRANSLATIONS: Record<ScheduleCancellationReason, string> = {
  [ScheduleCancellationReason.REGRET]: "Arrependimento",
  [ScheduleCancellationReason.FINANCIAL_ISSUES]: "Questões financeiras",
  [ScheduleCancellationReason.THIRD_PARTY_INFLUENCE]: "Influência de terceiros",
  [ScheduleCancellationReason.RESPONSIBLE_DISAGREEMENT]: "Desacordo do responsável",
  [ScheduleCancellationReason.TALENT_WITHDRAWAL]: "Desistência do talento",
  [ScheduleCancellationReason.PHOTO_DELAY]: "Atraso na fotografia",
  [ScheduleCancellationReason.MATERIAL_DELIVERY_DELAY]: "Atraso na entrega de material",
};

export const SCHEDULE_PAUSE_REASON_TRANSLATIONS: Record<SchedulePauseReason, string> = {
  [SchedulePauseReason.TALENT_UNWELL_OR_SLEEPING]: "Talento está indisposto ou dormindo",
  [SchedulePauseReason.TALENT_AGITATED]: "Talento está agitado",
  [SchedulePauseReason.TALENT_OR_RESPONSIBLE_HAS_COMMITMENT]: "Talento ou responsável tem compromisso",
  [SchedulePauseReason.TALENT_MEAL_BREAK]: "Talento fez pausa para alimentação",
};

export const SCHEDULE_PAUSE_REASON_OPTIONS = Object.values(SchedulePauseReason).map((value) => ({
  label: SCHEDULE_PAUSE_REASON_TRANSLATIONS[value],
  value,
}));

export const SCHEDULE_CANCELLATION_REASON_OPTIONS = Object.values(ScheduleCancellationReason).map((value) => ({
  label: SCHEDULE_CANCELLATION_REASON_TRANSLATIONS[value],
  value,
}));

export const SCHEDULE_DETAIL_NAVIGATION_ITEMS: readonly { label: string; path: RoutePathEnum }[] = [
  { label: "Dados do talento", path: RoutePathEnum.SCHEDULE_TALENT_DATA },
  { label: "Produção", path: RoutePathEnum.SCHEDULE_PRODUCTION },
  { label: "Contratos", path: RoutePathEnum.SCHEDULE_CONTRACTS },
  { label: "Produção artística", path: RoutePathEnum.SCHEDULE_ARTISTIC_PRODUCTION },
  { label: "Fotografia", path: RoutePathEnum.SCHEDULE_PHOTOGRAPHY },
];

export const PHOTO_RENEWAL_ALLOWED_NAVIGATION_PATHS: readonly RoutePathEnum[] = [
  RoutePathEnum.SCHEDULE_TALENT_DATA,
  RoutePathEnum.SCHEDULE_ARTISTIC_PRODUCTION,
  RoutePathEnum.SCHEDULE_PHOTOGRAPHY,
];

export const SCHEDULE_ABILITY_CATEGORIES = {
  [SkillCategoryEnum.LICENSE]: { label: "Habilitação", options: DriverLicenseEnum },
  [SkillCategoryEnum.SPORT]: { label: "Esporte", options: SportsEnum },
  [SkillCategoryEnum.MUSIC]: { label: "Música", options: MusicEnum },
  [SkillCategoryEnum.DANCE]: { label: "Dança", options: DanceEnum },
};

export const PHOTOGENY_RESULT_OPTIONS = [PhotogenyResultEnum.APPROVED, PhotogenyResultEnum.REJECTED].map((value) => ({
  value,
  label: PhotogenyResultTranslate[value],
}));

export const PHOTOGENY_REJECTION_REASON_OPTIONS = [
  PhotogenyRejectionReasonEnum.NO_PROFILE,
  PhotogenyRejectionReasonEnum.CRIED_TOO_MUCH,
  PhotogenyRejectionReasonEnum.MOVES_TOO_MUCH,
].map((value) => ({
  value,
  label: PhotogenyRejectionReasonTranslate[value],
}));
